import { useCallback, useId, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  labelledBy?: string;
  /** Largeur maximale : `md` par défaut (formulaires), `lg`/`xl` pour un lecteur ou un aperçu. */
  size?: 'md' | 'lg' | 'xl';
  /** False for a step the user must answer: no close button; the overlay and Escape still call `onClose`. */
  dismissible?: boolean;
}

const SIZE_CLASS: Record<NonNullable<ModalProps['size']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ isOpen, onClose, title, children, labelledBy, size = 'md', dismissible = true }: ModalProps) {
  const { t } = useTranslation('common');
  const generatedId = useId();
  const titleId = labelledBy ?? generatedId;

  useEscapeKey({ enabled: isOpen, onEscape: onClose });
  const { containerRef } = useFocusTrap<HTMLDivElement>({ enabled: isOpen });

  const handleContentClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="modal-root"
    >
      <button
        type="button"
        className="absolute inset-0 w-full h-full bg-slate-900/60 backdrop-blur-sm cursor-default"
        onClick={onClose}
        aria-label={t('a11y.modal.overlay_label')}
        tabIndex={-1}
        data-testid="modal-overlay"
      />
      <motion.div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28, mass: 0.8 }}
        className={`relative bg-white rounded-2xl shadow-2xl ${SIZE_CLASS[size]} w-full max-h-[90vh] overflow-y-auto focus:outline-none`}
        onClick={handleContentClick}
        data-testid="modal-content"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {title}
          </h2>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t('a11y.modal.close')}
              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors focus-visible:ring-2 focus-visible:ring-action/40"
            >
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="px-6 py-5">{children}</div>
      </motion.div>
    </div>,
    document.body,
  );
}
